import { useId, useState } from "react";
import Button from "./Button";
import Dialog from "./Dialog";
import Notice from "./Notice";

/**
 * plainError — a server's refusal in words a person can act on: the message
 * without transport prefixes ("Request failed with status code 409 — …") and
 * never a bare status code. Falls back to `fallback` when nothing usable came.
 */
export function plainError(error, fallback = "This couldn't be completed. Try again.") {
  const message = error?.serverMessage || error?.response?.data?.message || "";
  const text = String(message).replace(/^Request failed( with status code)?:?\s*\d{3}\s*[—–-]?\s*/i, "").trim();
  return text && !/^\d{3}$/.test(text) ? text : fallback;
}

/**
 * ConfirmDialog — the one confirmation for a consequential action
 * (DESIGN.md › Consequential actions and confirmations).
 *
 * It names the action in the title, shows the subject and the facts needed to
 * decide, states the consequence once, and offers a safe way back beside an
 * explicit verb. It owns only the mechanics every confirmation shares: one
 * submission at a time, progress on the action, the server's refusal kept in
 * the dialog with the context still visible, and retry. It holds no business
 * rule: the caller decides what the action is and the server whether it may
 * happen.
 *
 *   tone="danger"   only when the action destroys or revokes something.
 *                   Approving, paying or releasing is not danger.
 *   acknowledge     a sentence the person must tick first (C4 only), e.g.
 *                   "I understand this cannot be undone".
 *   confirmDisabled the action is unavailable until the caller's own input is valid
 *                   (e.g. a required reason typed into children).
 *   onConfirm       async; resolve to finish (the caller closes and refreshes),
 *                   throw to keep the dialog open with the reason.
 *
 * Focus never starts on the confirming action: it starts on the
 * acknowledgement when there is one, otherwise on Close, so a stray Enter
 * cannot confirm.
 */
export default function ConfirmDialog({
  title,
  description,
  facts,
  consequence,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  acknowledge,
  confirmDisabled = false,
  failureTitle = "Not done",
  errorFallback,
  size = "sm",
  onConfirm,
  onClose,
}) {
  const id = useId();
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);

  const confirm = async () => {
    if (pending || confirmDisabled || (acknowledge && !acknowledged)) return;
    setPending(true);
    setFailure("");
    try {
      await onConfirm();
    } catch (error) {
      setFailure(plainError(error, errorFallback));
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      size={size}
      onOpenChange={(open) => { if (!open && !pending) onClose(); }}
      title={title}
      description={description}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>{cancelLabel}</Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            loading={pending}
            disabled={confirmDisabled || (Boolean(acknowledge) && !acknowledged)}
            onClick={confirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div data-ui="confirm" data-tone={tone} className="space-y-3 text-body-sm text-secondary">
        {facts?.length ? (
          <dl className="divide-y divide-subtle border-y border-subtle">
            {facts.map((fact) => (
              <div key={fact.label} className="flex items-baseline justify-between gap-4 py-2 max-sm:flex-col max-sm:gap-0.5">
                <dt className="shrink-0 text-secondary">{fact.label}</dt>
                <dd className="min-w-0 break-words font-medium text-foreground sm:text-right">{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {children}
        {consequence ? <p>{consequence}</p> : null}
        {acknowledge ? (
          <label htmlFor={`${id}-ack`} className="flex min-h-touch cursor-pointer items-start gap-2.5 py-1 text-foreground">
            <input
              data-ui="checkbox"
              id={`${id}-ack`}
              type="checkbox"
              checked={acknowledged}
              disabled={pending}
              onChange={(event) => setAcknowledged(event.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
            />
            <span>{acknowledge}</span>
          </label>
        ) : null}
        {failure ? <Notice tone="danger" title={failureTitle} role="alert">{failure}</Notice> : null}
      </div>
    </Dialog>
  );
}
